import React, {useEffect, useMemo, useRef, useState} from "react";
import {Swiper, SwiperSlide, useSwiper} from "swiper/react";
import {Autoplay, Navigation, Pagination} from "swiper/modules";
import {useLocaleContext} from '../../../../context/LocaleContext';
import {useModalContext} from "../../../../context/ModalContext";
import getFullname from "../../../../helpers/get-fullname";
import { getSortedMembers } from "../../../../helpers/members";
import getMemberNickname from "../../../../helpers/get-member-nickname";
import {CTags} from "../../../UI";
import MemberDetailsModal from '../../MembersComponents/MemberDetailsModal';
import MemberPhoto from "../../MembersComponents/MemberPhoto";
import Icons from "../../../Icons";

import "./style.scss";

const SwiperControls = () => {
    const swiper = useSwiper();

    return (
        <div className="members-carousel-view-swiper__buttons">
            <button
                className="members-carousel-view-swiper__arrow"
                onClick={() => swiper.slidePrev()}
            >
                <Icons name="chevron_left" />
            </button>
            <button
                className="members-carousel-view-swiper__arrow"
                onClick={() => swiper.slideNext()}
            >
                <Icons name="chevron_right" />
            </button>
        </div>
    )
}

/**
 * @param {Object} event Object
 * @param {array} members Array of members
 * @param {boolean} showAutoplay Boolean value for 
 * @param {boolean} showArrow Boolean value for displaying arrows
 * @param {Object} settings Object with widget settings
 * @returns {reactComponent}
 */

const MembersCarouselView = ({
    event,
    members,
    showAutoplay = true,
    showArrow = true,
    isWidget = false,
    settings = null,
}) => {
    const $locale = useLocaleContext();
    const {renderModal} = useModalContext();
    const [slidesCount, setSlidesCount] = useState(0);
    const swiperRef = useRef(null);

    const visibleMembers = useMemo(() => {
        if (event.moduleEpisode.sortMembers) {
            return getSortedMembers(members, $locale, 'lastname', 'en')
        }

        return members;
    }, [members]);

    useEffect(calculateSlidesCount, []);

    useEffect(() => {
        swiperRef?.current?.swiper?.init();
    }, [members]);

    useEffect(() => {
        showAutoplay 
            ? swiperRef?.current?.swiper?.autoplay?.start()
            : swiperRef?.current?.swiper?.autoplay?.stop();
    }, [showAutoplay]);

    function handleMemberClick(member) {
        renderModal(MemberDetailsModal, {event, lockPageScroll: false, member, isWidget, settings});
    }

    function applySettings(key, condition = true) {
        if (!settings && condition) {
            return true;
        }

        return !!settings && !!settings?.[key];
    }

    function calculateSlidesCount() {
        let count = 6;

        if (window.innerWidth > 800 && window.innerWidth <= 1400) {
            count = 4;
        }

        if (window.innerWidth > 520 && window.innerWidth <= 800) {
            count = 3;
        }

        if (window.innerWidth <= 520) {
            count = 1;
        }

        setSlidesCount(count);
    }

    return (
        <div className="members-carousel-view">
            <Swiper
                ref={swiperRef}
                spaceBetween={20}
                slidesPerView={slidesCount}
                autoplay={{
                    delay: 3000,
                    disableOnInteraction: false,
                }}
                pagination={{
                    clickable: true,
                    bulletClass: `members-carousel-view-swiper__bullet ${members.length <= 1 ? 'hidden' : ''}`,
                    bulletActiveClass: 'active',
                }}
                modules={[Autoplay, Navigation, Pagination]}
                className={`members-carousel-view-swiper ${members.length < slidesCount ? 'members-carousel-view-swiper--centered' : ''}`}
            >
                {visibleMembers?.map((member) => (
                    <SwiperSlide key={member.id}>
                        <div className="members-carousel-view-details">
                            <div className="members-carousel-view-details__photo">
                                <MemberPhoto
                                    photo={member.profileUrl}
                                    initials={getMemberNickname(member, $locale)}
                                    size="auto"
                                    onClick={() => handleMemberClick(member)}
                                />
                            </div>
                            <div className="members-carousel-view-details__info">
                                {
                                    applySettings('showRoles') &&
                                    <CTags
                                        tags={member.roles}
                                        size="small"
                                    />
                                }
                                {
                                    applySettings('showFullname') &&
                                    <a
                                        href=""
                                        className="members-carousel-view-details__info-name"
                                        style={{fontWeight: applySettings('showFullnameBold') ? 'bold' : 'normal'}}
                                        onClick={(e) => {e.preventDefault(); onMemberClick(member);}}
                                    >
                                        {getFullname(member, $locale)}
                                    </a>
                                }
                                {
                                    applySettings('showOrganization') &&
                                    <div>
                                        <p
                                            className="members-carousel-view-details__info-text members-carousel-view-details__info-text--company"
                                            style={{fontWeight: applySettings('showOrganizationBold', false) ? 'bold' : ''}}
                                        >
                                            {$locale.getProperty(member, 'company')}
                                        </p>
                                    </div>
                                }
                                {
                                    applySettings('showRegalia') &&
                                    <p
                                        className="members-carousel-view-details__info-text"
                                        style={{fontWeight: applySettings('showRegaliaBold', false) ? 'bold' : ''}}
                                    >
                                        {$locale.getProperty(member, 'regalia')}
                                    </p>
                                }
                            </div>
                        </div>
                    </SwiperSlide>
                ))}

                {(members?.length > slidesCount && showArrow) && <SwiperControls />}
            </Swiper>
        </div>
    );
};

export default MembersCarouselView;
